import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axiosInstance from "../../api/axiosInstance";

const initialState = {
  matchDetails: null,
  fancies: null,
  fancyCategories: null,
  tvUrl: null,
  teamPosition: null,
  fancyLiability: null,
  loading: false,
  initialLoad: true,
  error: null,
  selectedBet: null,
};

export const fetchMatchDetailsAsync = createAsyncThunk(
  "game/fetchMatchDetails",
  async ({ matchId, marketId }, { rejectWithValue }) => {
    const payload = {
      combine: true,
      match_id: matchId,
    };
    if (marketId) {
      payload.market_id = marketId;
    }
    try {
      const response = await axiosInstance.post("/match/matchDetails", payload);
      return response.data || response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch match details",
      );
    }
  },
);

export const fetchFanciesAsync = createAsyncThunk(
  "game/fetchFancies",
  async ({ matchId, marketId }, { rejectWithValue }) => {
    const payload = {
      combine: true,
      match_id: matchId,
    };
    if (marketId) {
      payload.market_id = marketId;
    }
    try {
      const response = await axiosInstance.post("/fancy/getFancies", payload);
      const data = response.data || response;
      const fancyCategories =
        response?.data?.fancy_category ??
        response?.fancy_category ??
        data?.fancy_category ??
        null;
      return { data, fancyCategories };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch fancies",
      );
    }
  },
);

export const getPopupContentAsync = createAsyncThunk(
  "content/popContentGet",
  async ({ host }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/content/popContentGet?content_type=Popup&host=${host}`, {});
      return response.data || response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch popup content",
      );
    }
  },
);

export const fetchTvUrlAsync = createAsyncThunk(
  "game/fetchTvUrl",
  async (matchId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        "/match/getTvUrlScoreboardUrl",
        {
          match_id: matchId,
        },
      );
      return response.data || response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch TV URL",
      );
    }
  },
);

export const fetchTeamPositionAsync = createAsyncThunk(
  "game/fetchTeamPosition",
  async ({matchId, marketId}, { rejectWithValue }) => {
    const payload = {
      match_id: matchId,
    };
    if (marketId) {
      payload.market_id = marketId;
    }
    try {
      const response = await axiosInstance.post("/bet/getTeamPosition", payload);
      return response.data || response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch team position",
      );
    }
  },
);

export const fetchFancyLiabilityAsync = createAsyncThunk(
  "game/fetchFancyLiability",
  async ({matchId, marketId}, { rejectWithValue }) => {
    const payload = {
      match_id: matchId,
    };
    if (marketId) {
      payload.market_id = marketId;
    }
    try {
      const response = await axiosInstance.post("/bet/getFancyLiability", payload);
      return response.data || response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch fancy liability",
      );
    }
  },
);

export const fetchRunPositionAsync = createAsyncThunk(
  "game/fetchRunPosition",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        "/fancy/getRunTimeFancyPosition",
        payload,
      );

      return response.data || response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch run position",
      );
    }
  },
);

const gameSlice = createSlice({
  name: "game",
  initialState: {
    ...initialState,
    runPosition: null,
  },
  reducers: {
    clearGameData: (state) => {
      state.matchDetails = null;
      state.fancies = null;
      state.tvUrl = null;
      state.teamPosition = null;
      state.fancyLiability = null;
      state.error = null;
      state.initialLoad = true;
      state.selectedBet = null;
    },
    setSelectedBet: (state, action) => {
      state.selectedBet = {
        selectionName: action.payload?.selectionName || "",
        marketTitle: action.payload?.marketTitle || "",
        side: action.payload?.side || "back",
        minStake:
          typeof action.payload?.minStake === "number"
            ? action.payload.minStake
            : action.payload?.minStake != null
              ? Number(action.payload.minStake) || null
              : null,
        maxStake:
          typeof action.payload?.maxStake === "number"
            ? action.payload.maxStake
            : action.payload?.maxStake != null
              ? Number(action.payload.maxStake) || null
              : null,
        odds: typeof action.payload?.odds === "number"
          ? action.payload.odds
          : Number(action.payload?.odds) || 0,
        stake:
          typeof action.payload?.stake === "number" ||
          typeof action.payload?.stake === "string"
            ? action.payload.stake
            : "",
        isFancy: !!action.payload?.isFancy,
        fancyId: action.payload?.fancyId || null,
        marketId: action.payload?.marketId || null,
        selectionId: action.payload?.selectionId || null,
        runs:
          typeof action.payload?.runs === "number" ||
          typeof action.payload?.runs === "string"
            ? action.payload.runs
            : "",
        size:
          typeof action.payload?.size === "number" ||
          typeof action.payload?.size === "string"
            ? action.payload.size
            : "",
      };
    },
    updateSelectedBetOdds: (state, action) => {
      if (!state.selectedBet) return;
      const next =
        typeof action.payload === "number"
          ? action.payload
          : Number(action.payload) || 0;
      state.selectedBet.odds = next;
    },
    updateSelectedBetStake: (state, action) => {
      if (!state.selectedBet) {
        state.selectedBet = {
          selectionName: "",
          marketTitle: "",
          side: "back",
          odds: 0,
          stake: action.payload,
        };
        return;
      }
      state.selectedBet.stake = action.payload;
    },
    clearSelectedBet: (state) => {
      state.selectedBet = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMatchDetailsAsync.pending, (state) => {
        if (state.initialLoad) {
          state.loading = true;
        }
        state.error = null;
      })
      .addCase(fetchMatchDetailsAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.initialLoad = false;
        state.matchDetails = action.payload;
        state.error = null;
      })
      .addCase(fetchMatchDetailsAsync.rejected, (state, action) => {
        state.loading = false;
        state.initialLoad = false;
        state.error = action.payload;
      })
      .addCase(fetchFanciesAsync.pending, (state) => {
        state.error = null;
      })
        .addCase(fetchFanciesAsync.fulfilled, (state, action) => {
          state.fancies = action.payload.data;
          state.fancyCategories = action.payload.fancyCategories;
          state.error = null;
      })
      .addCase(fetchFanciesAsync.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(fetchTvUrlAsync.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchTvUrlAsync.fulfilled, (state, action) => {
        state.tvUrl = action.payload;
        state.error = null;
      })
      .addCase(fetchTvUrlAsync.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(fetchTeamPositionAsync.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchTeamPositionAsync.fulfilled, (state, action) => {
        state.teamPosition = action.payload;
        state.error = null;
      })
      .addCase(fetchTeamPositionAsync.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(fetchFancyLiabilityAsync.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchFancyLiabilityAsync.fulfilled, (state, action) => {
        state.fancyLiability = action.payload;
        state.error = null;
      })
      .addCase(fetchFancyLiabilityAsync.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(fetchRunPositionAsync.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchRunPositionAsync.fulfilled, (state, action) => {
        state.runPosition = action.payload;
        state.error = null;
      })
      .addCase(fetchRunPositionAsync.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const {
  clearGameData,
  setSelectedBet,
  updateSelectedBetOdds,
  updateSelectedBetStake,
  clearSelectedBet,
} = gameSlice.actions;
export default gameSlice.reducer;
